import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../AuthContext';
import { SELLER_PLAN, PRICE_PER_LOCATION, planLabel, planPrice } from '../pricing';
import { US_TIMEZONES } from '../dateUtil';
import { formatCurrency } from '../currencyUtil';

// ─── Subscription ─────────────────────────────────────────────────────────────

function SubscriptionTab({ account, locationCount, onUpdate }) {
  const activeTier   = account?.subscription_tier ?? null;
  const pendingTier  = account?.pending_tier ?? null;
  const [selected, setSelected] = useState(activeTier);
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);
  const navigate = useNavigate();

  const save = async () => {
    if (activeTier === null && selected !== null) {
      navigate(`/upgrade?plan=${selected}`);
      return;
    }
    setSaving(true);
    try {
      const updated = await api.put(`/accounts/${account.id}`, {
        name: account.name,
        subscription_tier: selected,
      });
      onUpdate(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  const unchanged = selected === activeTier;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 text-sm py-1">
        <span className={`w-2 h-2 rounded-full shrink-0 ${activeTier ? 'bg-emerald-500' : pendingTier ? 'bg-amber-400' : 'bg-slate-300 dark:bg-slate-600'}`} />
        {activeTier
          ? <><span className="font-medium text-slate-700 dark:text-slate-300">Active</span><span className="text-slate-400 dark:text-slate-500">— {planLabel(activeTier)}</span></>
          : pendingTier
          ? <><span className="font-medium text-amber-600 dark:text-amber-400">Pending approval</span><span className="text-slate-400 dark:text-slate-500">— {planLabel(pendingTier)} requested</span></>
          : <span className="text-slate-500 dark:text-slate-400">No active subscription — seller features are disabled</span>
        }
      </div>

      {pendingTier && (
        <div className="rounded border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-700 dark:text-amber-300">
          Your subscription request is awaiting admin approval. You'll receive a notification when it's confirmed.
        </div>
      )}

      <div className={`space-y-2 ${pendingTier ? 'opacity-50 pointer-events-none' : ''}`}>
        <label className={`flex items-start gap-3 p-4 rounded border cursor-pointer transition-colors ${
          selected === SELLER_PLAN
            ? 'border-blue-400 bg-blue-50 dark:border-blue-600 dark:bg-blue-900/20'
            : 'border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500'
        }`}>
          <input type="radio" name="tier" value={SELLER_PLAN} checked={selected === SELLER_PLAN}
            onChange={() => setSelected(SELLER_PLAN)} className="mt-0.5 accent-blue-600 shrink-0" />
          <div className="flex-1">
            <div className="flex items-baseline justify-between gap-2">
              <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{planLabel(SELLER_PLAN)}</p>
              <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                ${formatCurrency(planPrice(locationCount))}/mo
              </p>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              ${formatCurrency(PRICE_PER_LOCATION)} per location × {locationCount} location{locationCount !== 1 ? 's' : ''}. Unlimited users at each location.
            </p>
          </div>
        </label>
        <label className={`flex items-start gap-3 p-4 rounded border cursor-pointer transition-colors ${
          selected === null
            ? 'border-slate-400 bg-slate-50 dark:border-slate-500 dark:bg-slate-800'
            : 'border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500'
        }`}>
          <input type="radio" name="tier" value="" checked={selected === null}
            onChange={() => setSelected(null)} className="mt-0.5 accent-blue-600 shrink-0" />
          <div>
            <p className="text-sm font-medium text-slate-800 dark:text-slate-100">None</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Buyer access only — seller features disabled.</p>
          </div>
        </label>
      </div>

      {!pendingTier && (
        <div className="flex items-center gap-3">
          <button onClick={save} disabled={saving || unchanged} className="btn-primary disabled:opacity-50">
            {saving ? 'Saving…' : saved ? 'Saved' : 'Save changes'}
          </button>
          {!unchanged && <button onClick={() => setSelected(activeTier)} className="btn-secondary">Cancel</button>}
        </div>
      )}

    </div>
  );
}

// ─── Locations ────────────────────────────────────────────────────────────────

function LocationsTab({ account, locations, onLocationsChange, currentLocationId }) {
  const [adding, setAdding]       = useState(false);
  const [name, setName]           = useState('');
  const [address, setAddress]     = useState('');
  const [zip, setZip]             = useState('');
  const [taxRate, setTaxRate]     = useState('');
  const [timezone, setTimezone]   = useState('Pacific/Honolulu');
  const [disclosure, setDisclosure] = useState('');
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');

  const [deleteError, setDeleteError] = useState('');

  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError]   = useState('');

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim() || !address.trim() || !zip.trim()) return;
    setSaving(true);
    setError('');
    try {
      const loc = await api.post('/locations', {
        account_id: account.id, name: name.trim(), address: address.trim(), zip_code: zip.trim(),
        default_tax_rate: taxRate === '' ? 0 : taxRate,
        default_seller_disclosure: disclosure.trim(),
        timezone,
      });
      onLocationsChange(locs => [...locs, loc]);
      setName(''); setAddress(''); setZip(''); setTaxRate(''); setTimezone('Pacific/Honolulu'); setDisclosure(''); setAdding(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (loc) => {
    setEditingId(loc.id);
    setEditDraft({
      name: loc.name || '', address: loc.address || '', zip_code: loc.zip_code || '',
      default_tax_rate: loc.default_tax_rate ?? 0,
      default_seller_disclosure: loc.default_seller_disclosure || '',
      timezone: loc.timezone || 'Pacific/Honolulu',
    });
    setEditError('');
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editDraft.name.trim()) return;
    setEditSaving(true);
    setEditError('');
    try {
      const updated = await api.put(`/locations/${editingId}`, {
        name: editDraft.name.trim(), address: editDraft.address.trim(), zip_code: editDraft.zip_code.trim(),
        default_tax_rate: editDraft.default_tax_rate === '' ? 0 : editDraft.default_tax_rate,
        default_seller_disclosure: editDraft.default_seller_disclosure.trim(),
        timezone: editDraft.timezone,
      });
      onLocationsChange(locs => locs.map(l => l.id === editingId ? updated : l));
      setEditingId(null);
      setEditDraft(null);
    } catch (err) {
      setEditError(err.message);
    } finally {
      setEditSaving(false);
    }
  };

  const remove = async (loc) => {
    // Deleting a location cascades to every user assigned to it — their account
    // record is removed entirely, not just unassigned. If that includes the user
    // performing the deletion, warn explicitly before they lock themselves out.
    const message = loc.id === currentLocationId
      ? `You are currently assigned to "${loc.name}". Deleting this location will also permanently remove your own account data, and you will be signed out and required to create or join a new account. If this is not what you intend, please reassign yourself to a different location before deleting this one.\n\nDelete "${loc.name}" anyway?`
      : `Delete location "${loc.name}"? All users currently assigned to this location will have their account data permanently removed, not just unassigned.`;
    if (!window.confirm(message)) return;
    setDeleteError('');
    try {
      await api.del(`/locations/${loc.id}`);
      onLocationsChange(locs => locs.filter(l => l.id !== loc.id));
    } catch (err) {
      setDeleteError(err.message);
    }
  };

  return (
    <div className="space-y-5">
      <p className="text-xs text-slate-400 dark:text-slate-500">
        Share a lot's code with a seller you want to buy from — they'll enter it under their Buyers tab to connect.
      </p>
      {deleteError && <p className="text-xs text-red-600 dark:text-red-400">{deleteError}</p>}
      <div className="border border-slate-200 dark:border-slate-700 rounded overflow-x-auto">
        {locations.length === 0 ? (
          <p className="px-4 py-6 text-sm text-slate-400 dark:text-slate-500 text-center">No locations yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Lot Code</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Name</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Address</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Default Tax</th>
                <th className="w-20 px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {locations.map(loc => (
                editingId === loc.id ? (
                  <tr key={loc.id}>
                    <td colSpan={5} className="p-0">
                      <form onSubmit={saveEdit} className="p-4 space-y-3 bg-slate-50 dark:bg-slate-800/50">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="label">Name</label>
                            <input className="input" value={editDraft.name}
                              onChange={e => setEditDraft(d => ({ ...d, name: e.target.value }))} required autoFocus />
                          </div>
                          <div className="col-span-2">
                            <label className="label">Street Address</label>
                            <input className="input" placeholder="123 Main St, Salt Lake City, UT" value={editDraft.address}
                              onChange={e => setEditDraft(d => ({ ...d, address: e.target.value }))} required />
                            <p className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">Shown to buyers on deals and printed on deal records. Each physical lot needs its own location.</p>
                          </div>
                          <div>
                            <label className="label">ZIP Code</label>
                            <input className="input" value={editDraft.zip_code}
                              onChange={e => setEditDraft(d => ({ ...d, zip_code: e.target.value }))} />
                          </div>
                          <div>
                            <label className="label">Default Tax Rate</label>
                            <div className="relative">
                              <input className="input pr-7" type="number" min="0" max="100" step="0.01"
                                value={editDraft.default_tax_rate}
                                onChange={e => setEditDraft(d => ({ ...d, default_tax_rate: e.target.value }))} />
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
                            </div>
                          </div>
                          <div>
                            <label className="label">Timezone</label>
                            <select className="input" value={editDraft.timezone}
                              onChange={e => setEditDraft(d => ({ ...d, timezone: e.target.value }))}>
                              {US_TIMEZONES.map(tz => <option key={tz.id} value={tz.id}>{tz.label}</option>)}
                            </select>
                            <p className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">Deals stay open through the end of their end date in this timezone.</p>
                          </div>
                        </div>
                        <div>
                          <label className="label">Default Seller Disclosure</label>
                          <textarea className="input resize-none" rows={2}
                            placeholder="Prepopulates onto new cars added at this lot — editable per car."
                            value={editDraft.default_seller_disclosure}
                            onChange={e => setEditDraft(d => ({ ...d, default_seller_disclosure: e.target.value }))} />
                        </div>
                        {editError && <p className="text-xs text-red-600">{editError}</p>}
                        <div className="flex items-center gap-2">
                          <button type="submit" disabled={editSaving} className="btn-primary">
                            {editSaving ? 'Saving…' : 'Save'}
                          </button>
                          <button type="button" onClick={() => { setEditingId(null); setEditDraft(null); }} className="btn-secondary">
                            Cancel
                          </button>
                          <button type="button" onClick={() => remove(loc)} className="ml-auto text-xs text-red-500 hover:text-red-600 transition-colors">
                            Delete
                          </button>
                        </div>
                      </form>
                    </td>
                  </tr>
                ) : (
                  <tr key={loc.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30">
                    <td className="px-4 py-3 font-mono text-xs text-slate-500 dark:text-slate-400">{loc.lot_code}</td>
                    <td className="px-4 py-3 font-medium text-slate-800 dark:text-slate-100">{loc.name}</td>
                    <td className="px-4 py-3 text-slate-500 dark:text-slate-400">
                      {loc.address
                        ? [loc.address, loc.zip_code].filter(Boolean).join(' ')
                        : <span className="text-amber-600 dark:text-amber-400">Add an address</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-500 dark:text-slate-400">{Number(loc.default_tax_rate || 0)}%</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => startEdit(loc)}
                        className="p-2 -m-2 text-xs text-slate-400 hover:text-blue-500 transition-colors"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                )
              ))}
            </tbody>
          </table>
        )}
      </div>

      {adding ? (
        <form onSubmit={add} className="border border-slate-200 dark:border-slate-700 rounded p-4 space-y-3 bg-slate-50 dark:bg-slate-800/50">
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">New Location</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Name</label>
              <input className="input" placeholder="Main Lot" value={name} onChange={e => setName(e.target.value)} required autoFocus />
            </div>
            <div className="col-span-2">
              <label className="label">Street Address</label>
              <input className="input" placeholder="123 Main St, Salt Lake City, UT" value={address} onChange={e => setAddress(e.target.value)} required />
              <p className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">Shown to buyers on deals and printed on deal records. Each physical lot needs its own location.</p>
            </div>
            <div>
              <label className="label">ZIP Code</label>
              <input className="input" placeholder="84101" value={zip} onChange={e => setZip(e.target.value)} required />
            </div>
            <div>
              <label className="label">Default Tax Rate</label>
              <div className="relative">
                <input className="input pr-7" type="number" min="0" max="100" step="0.01"
                  placeholder="0" value={taxRate} onChange={e => setTaxRate(e.target.value)} />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
              </div>
            </div>
            <div>
              <label className="label">Timezone</label>
              <select className="input" value={timezone} onChange={e => setTimezone(e.target.value)}>
                {US_TIMEZONES.map(tz => <option key={tz.id} value={tz.id}>{tz.label}</option>)}
              </select>
              <p className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">Deals stay open through the end of their end date in this timezone.</p>
            </div>
          </div>
          <div>
            <label className="label">Default Seller Disclosure</label>
            <textarea className="input resize-none" rows={2}
              placeholder="Prepopulates onto new cars added at this lot — editable per car."
              value={disclosure} onChange={e => setDisclosure(e.target.value)} />
          </div>
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex items-center gap-2">
            <button type="submit" disabled={saving} className="btn-primary">
              {saving ? 'Adding…' : 'Add location'}
            </button>
            <button type="button" onClick={() => { setAdding(false); setName(''); setAddress(''); setZip(''); setTaxRate(''); setDisclosure(''); setError(''); }} className="btn-secondary">
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <button onClick={() => setAdding(true)} className="btn-secondary flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add location
        </button>
      )}
    </div>
  );
}

// ─── Buyer Connections (seller view) ──────────────────────────────────────────
// Relationships are lot-to-lot and managed only from the seller lot's own view —
// each admin is scoped to their own lot, so there's no cross-lot picker here.

function BuyersTab({ locationId, locationName }) {
  const [connections, setConnections] = useState([]);
  const [lotIdInput, setLotIdInput]   = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const [looking, setLooking]         = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [adding, setAdding]           = useState(false);
  const [removing, setRemoving]       = useState({});
  const [loading, setLoading]         = useState(true);

  const fetchConnections = useCallback(async () => {
    if (!locationId) return;
    setLoading(true);
    try {
      setConnections(await api.get(`/location-relationships?seller_location_id=${locationId}`));
    } finally { setLoading(false); }
  }, [locationId]);

  useEffect(() => { fetchConnections(); }, [fetchConnections]);

  const lookup = async (e) => {
    e.preventDefault();
    if (!lotIdInput.trim()) return;
    setLooking(true);
    setLookupError('');
    setLookupResult(null);
    try {
      const loc = await api.get(
        `/location-relationships/lookup?seller_location_id=${locationId}&lot_code=${encodeURIComponent(lotIdInput.trim())}`
      );
      setLookupResult(loc);
    } catch (err) {
      setLookupError(err.message);
    } finally {
      setLooking(false);
    }
  };

  const add = async () => {
    if (!lookupResult) return;
    setAdding(true);
    try {
      const rel = await api.post('/location-relationships', {
        seller_location_id: locationId,
        lot_code: lookupResult.lot_code,
      });
      setConnections(c => [...c, { ...lookupResult, relationship_id: rel.id }]);
      setLookupResult(null);
      setLotIdInput('');
    } catch (err) {
      setLookupError(err.message);
    } finally {
      setAdding(false);
    }
  };

  const remove = async (conn) => {
    if (!window.confirm(`Remove "${conn.name}" (${conn.account_name}) from your buyer connections?`)) return;
    setRemoving(r => ({ ...r, [conn.relationship_id]: true }));
    try {
      await api.del(`/location-relationships/${conn.relationship_id}`);
      setConnections(c => c.filter(x => x.relationship_id !== conn.relationship_id));
    } finally { setRemoving(r => ({ ...r, [conn.relationship_id]: false })); }
  };

  if (!locationId) {
    return <p className="text-sm text-slate-400 dark:text-slate-500">You must be assigned to a lot to manage buyer connections.</p>;
  }

  return (
    <div className="space-y-6">
      <p className="text-xs text-slate-400 dark:text-slate-500">
        Managing buyer connections for <span className="font-medium text-slate-600 dark:text-slate-300">{locationName}</span>.
        Only this lot's cars and deals will be visible to lots connected below.
      </p>

      <div>
        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">
          Connected Buyer Lots{connections.length > 0 && ` (${connections.length})`}
        </p>
        {loading ? (
          <div className="flex justify-center py-6">
            <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : connections.length === 0 ? (
          <p className="text-sm text-slate-400 dark:text-slate-500">No buyer lots connected yet.</p>
        ) : (
          <div className="border border-slate-200 dark:border-slate-700 rounded overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Lot Code</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Lot</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Company</th>
                  <th className="w-16 px-4 py-2.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {connections.map(conn => (
                  <tr key={conn.relationship_id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 group">
                    <td className="px-4 py-3 font-mono text-xs text-slate-500 dark:text-slate-400">{conn.lot_code}</td>
                    <td className="px-4 py-3 font-medium text-slate-800 dark:text-slate-100">{conn.name}</td>
                    <td className="px-4 py-3 text-slate-500 dark:text-slate-400">{conn.account_name}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => remove(conn)}
                        disabled={removing[conn.relationship_id]}
                        className="md:opacity-0 md:group-hover:opacity-100 p-2 -m-2 text-xs text-slate-400 hover:text-red-500 transition-all disabled:opacity-50"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div>
        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">Add Buyer Lot</p>
        <p className="text-xs text-slate-400 dark:text-slate-500 mb-2">
          Ask the buyer for their Lot Code (shown on their Settings → Locations tab), then enter it below.
        </p>
        <form onSubmit={lookup} className="flex items-center gap-2 max-w-sm">
          <input
            className="input font-mono uppercase"
            type="text"
            maxLength={6}
            placeholder="Lot Code (e.g. K7QX2M)"
            value={lotIdInput}
            onChange={e => { setLotIdInput(e.target.value); setLookupResult(null); setLookupError(''); }}
          />
          <button type="submit" disabled={!lotIdInput.trim() || looking} className="btn-secondary shrink-0">
            {looking ? 'Finding…' : 'Find'}
          </button>
        </form>

        {lookupError && <p className="text-xs text-red-600 mt-2">{lookupError}</p>}

        {lookupResult && (
          <div className="mt-2 flex items-center gap-3 px-4 py-3 border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 rounded max-w-sm">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{lookupResult.name}</p>
              <p className="text-xs text-slate-400">{lookupResult.account_name} · {lookupResult.lot_code}</p>
            </div>
            <button onClick={add} disabled={adding}
              className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline shrink-0">
              {adding ? 'Adding…' : 'Add'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Team ─────────────────────────────────────────────────────────────────────

function TeamTab({ account, locations }) {
  const { appUser } = useAuth();
  const [members, setMembers]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [inviteEmail, setInviteEmail]   = useState('');
  const [inviteLocation, setInviteLocation] = useState('');
  const [invites, setInvites]           = useState([]);
  const [inviteSaving, setInviteSaving] = useState(false);
  const [inviteError, setInviteError]   = useState('');
  const [togglingAdmin, setTogglingAdmin] = useState({});
  const [memberError, setMemberError]   = useState('');

  const fetchMembers = useCallback(async () => {
    if (!account?.id) return;
    const rows = await api.get(`/users?account_id=${account.id}`);
    setMembers(rows);
    setLoading(false);
  }, [account?.id]);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  const fetchInvites = useCallback(async () => {
    if (!account?.id) return;
    try { setInvites(await api.get(`/invites?account_id=${account.id}`)); } catch {}
  }, [account?.id]);

  useEffect(() => { fetchInvites(); }, [fetchInvites]);

  useEffect(() => {
    if (locations.length === 1) setInviteLocation(String(locations[0].id));
  }, [locations]);

  const remove = async (user) => {
    const adminCount = members.filter(m => m.is_admin).length;
    if (user.is_admin && adminCount <= 1) {
      setMemberError('Cannot remove the last admin. Promote someone else first.');
      return;
    }
    if (!window.confirm(`Remove ${user.fname || user.email} from this account?`)) return;
    setMemberError('');
    try {
      await api.put(`/users/${user.id}`, { ...user, location_id: null, is_admin: false });
      await fetchMembers();
    } catch (err) {
      setMemberError(err.message);
    }
  };

  const toggleAdmin = async (user) => {
    const makingAdmin = !user.is_admin;
    const label = user.fname || user.email;
    if (!makingAdmin && members.filter(m => m.is_admin).length <= 1) {
      setMemberError('Cannot remove the last admin. Promote someone else first.');
      return;
    }
    const confirmed = window.confirm(
      makingAdmin
        ? `Make ${label} an admin? They'll be able to manage team members, locations, and subscription.`
        : `Remove admin access from ${label}?`
    );
    if (!confirmed) return;
    setMemberError('');
    setTogglingAdmin(t => ({ ...t, [user.id]: true }));
    try {
      await api.put(`/users/${user.id}`, { ...user, is_admin: makingAdmin });
      await fetchMembers();
    } catch (err) {
      setMemberError(err.message);
    } finally {
      setTogglingAdmin(t => ({ ...t, [user.id]: false }));
    }
  };

  const sendInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim() || !inviteLocation) return;
    setInviteSaving(true);
    setInviteError('');
    try {
      const inv = await api.post('/invites', {
        email: inviteEmail.trim().toLowerCase(),
        account_id: account.id,
        location_id: Number(inviteLocation),
      });
      const loc = locations.find(l => l.id === Number(inviteLocation));
      setInvites(i => [{ ...inv, location_name: loc?.name }, ...i]);
      setInviteEmail('');
      setInviteLocation('');
    } catch (err) {
      setInviteError(err.message);
    } finally {
      setInviteSaving(false);
    }
  };

  const cancelInvite = async (invite) => {
    setInviteError('');
    try {
      await api.del(`/invites/${invite.id}`);
      setInvites(i => i.filter(x => x.id !== invite.id));
    } catch (err) {
      setInviteError(err.message);
    }
  };

  return (
    <div className="space-y-6">
      {/* Invite member */}
      <div>
        <div>
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">Invite a team member</p>
          <form onSubmit={sendInvite} className="flex items-end gap-2 flex-wrap max-w-lg">
            <div className="flex-1 min-w-48">
              <label className="label">Email address</label>
              <input className="input" type="email" placeholder="name@example.com"
                value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} required />
            </div>
            {locations.length > 1 && (
              <div className="w-36">
                <label className="label">Location</label>
                <select className="input" value={inviteLocation} onChange={e => setInviteLocation(e.target.value)} required>
                  <option value="" disabled>Pick…</option>
                  {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
            )}
            <button type="submit" disabled={inviteSaving || !inviteEmail.trim() || (!inviteLocation && locations.length > 1)}
              className="btn-primary shrink-0">
              {inviteSaving ? 'Sending…' : 'Send Invite'}
            </button>
          </form>
          {inviteError && <p className="text-xs text-red-600 mt-1">{inviteError}</p>}

          {invites.length > 0 && (
            <div className="mt-3">
              <p className="text-xs text-slate-400 dark:text-slate-500 mb-1.5">Pending invites — each holds a seat until it's accepted, declined or cancelled. They'll be asked to accept the next time they sign in:</p>
              <div className="border border-slate-200 dark:border-slate-700 rounded overflow-hidden max-w-lg">
                {invites.map(inv => (
                  <div key={inv.id} className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-100 dark:border-slate-700 last:border-0">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-800 dark:text-slate-100">{inv.email}</p>
                      <p className="text-xs text-slate-400">{inv.location_name}</p>
                    </div>
                    <button onClick={() => cancelInvite(inv)} className="text-xs text-slate-400 hover:text-red-500 transition-colors">Cancel</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Member list */}
      <div>
        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">
          Team members{members.length > 0 && ` (${members.length})`}
        </p>
        {loading ? (
          <div className="flex justify-center py-6">
            <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : members.length === 0 ? (
          <p className="text-sm text-slate-400 dark:text-slate-500">No team members yet.</p>
        ) : (
          <div className="border border-slate-200 dark:border-slate-700 rounded overflow-hidden">
            {memberError && (
              <p className="px-4 py-2 text-xs text-red-600 dark:text-red-400 border-b border-slate-100 dark:border-slate-700">{memberError}</p>
            )}
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Name</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider hidden md:table-cell">Email</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Location</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Admin</th>
                  <th className="w-16 px-4 py-2.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {members.map(m => (
                  <tr key={m.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 group">
                    <td className="px-4 py-3">
                      <span className="font-medium text-slate-800 dark:text-slate-100">
                        {m.fname && m.lname ? `${m.fname} ${m.lname}` : m.email}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-500 dark:text-slate-400 hidden md:table-cell">{m.email}</td>
                    <td className="px-4 py-3 text-slate-500 dark:text-slate-400">{m.location_name || '—'}</td>
                    <td className="px-4 py-3">
                      {m.id === appUser?.id ? (
                        m.is_admin && <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">Admin</span>
                      ) : (
                        <label className="inline-flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!m.is_admin}
                            disabled={togglingAdmin[m.id]}
                            onChange={() => toggleAdmin(m)}
                            className="w-3.5 h-3.5 accent-blue-600 disabled:opacity-50"
                          />
                          <span className="text-xs text-slate-500 dark:text-slate-400">Admin</span>
                        </label>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {!m.is_admin && (
                        <button onClick={() => remove(m)}
                          className="md:opacity-0 md:group-hover:opacity-100 p-2 -m-2 text-xs text-slate-400 hover:text-red-500 transition-all">
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Notifications (personal — every user, not just admins) ──────────────────

const NOTIFICATION_OPTIONS = [
  { key: 'bids',         label: 'Bids & offers', desc: 'New bids, counter-offers, and accepted/rejected bids.' },
  { key: 'transactions', label: 'Transactions',  desc: 'Payment, inspection, and pickup updates.' },
  { key: 'subscription', label: 'Subscription',  desc: 'Approval or rejection of a subscription request.' },
];

function NotificationsTab() {
  const { appUser, setAppUser } = useAuth();
  const [prefs, setPrefs] = useState(appUser?.notification_preferences || { bids: true, transactions: true, subscription: true });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);

  const toggle = (key) => setPrefs(p => ({ ...p, [key]: !p[key] }));

  const save = async () => {
    setSaving(true);
    try {
      const updated = await api.put('/users/me/notification-preferences', prefs);
      setAppUser(u => ({ ...u, notification_preferences: updated.notification_preferences }));
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <p className="text-xs text-slate-400 dark:text-slate-500">
        These control email notifications only — everything still shows up in your in-app notification bell.
      </p>
      <div className="space-y-2">
        {NOTIFICATION_OPTIONS.map(o => (
          <label key={o.key} className="flex items-start gap-3 p-4 rounded border border-slate-200 dark:border-slate-600 cursor-pointer">
            <input type="checkbox" checked={prefs[o.key] !== false} onChange={() => toggle(o.key)}
              className="mt-0.5 accent-blue-600 shrink-0" />
            <div>
              <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{o.label}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{o.desc}</p>
            </div>
          </label>
        ))}
      </div>
      <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-50">
        {saving ? 'Saving…' : saved ? 'Saved' : 'Save changes'}
      </button>
    </div>
  );
}

// ─── Leave team (personal — every user) ───────────────────────────────────────

function LeaveTeam({ accountName }) {
  const [error, setError] = useState('');

  const leave = async () => {
    if (!window.confirm(`Leave ${accountName || 'this account'}? Your pending bids will be withdrawn and you'll need a new invite to come back.`)) return;
    setError('');
    try {
      await api.post('/users/me/leave');
      window.location.reload();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="mt-10 pt-6 border-t border-slate-200 dark:border-slate-700">
      <button onClick={leave} className="text-xs text-slate-400 hover:text-red-500 transition-colors">Leave this team</button>
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
}

// ─── Support & Feedback (personal — every user) ───────────────────────────────

function SupportTab() {
  const [type, setType]       = useState('support');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent]       = useState(false);
  const [error, setError]     = useState('');

  const send = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setSending(true);
    setError('');
    try {
      await api.post('/support', { type, message: message.trim() });
      setMessage('');
      setSent(true);
      setTimeout(() => setSent(false), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4 max-w-lg">
      <div className="flex gap-1 bg-slate-100 dark:bg-slate-800 rounded-lg p-1 w-fit">
        {['support', 'feedback'].map(t => (
          <button key={t} type="button" onClick={() => setType(t)}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold capitalize transition-colors ${
              type === t ? 'bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 shadow-sm' : 'text-slate-500 dark:text-slate-400'
            }`}>
            {t}
          </button>
        ))}
      </div>
      <form onSubmit={send} className="space-y-3">
        <textarea className="input resize-none" rows={5}
          placeholder={type === 'support' ? "Describe the issue you're running into…" : "Tell us what you think, or what you'd like to see…"}
          value={message} onChange={e => setMessage(e.target.value)} required />
        {error && <p className="text-xs text-red-600">{error}</p>}
        <button type="submit" disabled={sending || !message.trim()} className="btn-primary disabled:opacity-50">
          {sending ? 'Sending…' : sent ? 'Sent!' : `Send ${type === 'support' ? 'Support Request' : 'Feedback'}`}
        </button>
      </form>
    </div>
  );
}

// ─── Settings page ────────────────────────────────────────────────────────────

export default function Settings() {
  const { appUser, setAppUser }     = useAuth();
  const navigate                    = useNavigate();
  const [account, setAccount]       = useState(appUser?.account);
  const [locations, setLocations]   = useState([]);
  const [tab, setTab]               = useState('Subscription');
  const isAdmin          = !!appUser?.is_admin;
  const hasSubscription  = account?.subscription_tier != null;

  useEffect(() => {
    if (appUser?.account_id) {
      api.get(`/locations?account_id=${appUser.account_id}`).then(setLocations);
    }
  }, [appUser?.account_id]);

  const handleAccountUpdate = (updated) => {
    setAccount(a => ({ ...a, ...updated }));
    setAppUser(u => ({ ...u, account: { ...u.account, ...updated } }));
  };

  const adminTabs = isAdmin
    ? (hasSubscription ? ['Subscription', 'Locations', 'Team', 'Buyers'] : ['Subscription', 'Team', 'Locations'])
    : [];
  const tabs = [...adminTabs, 'Notifications', 'Support'];

  // Reset to first available tab if current tab is removed
  const activeTab = tabs.includes(tab) ? tab : tabs[0];

  return (
    <div className="max-w-3xl mx-auto px-6 py-8">
      <div className="mb-6">
        <h1 className="text-lg font-semibold text-slate-900 dark:text-white">Account Settings</h1>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{account?.name}</p>
      </div>

      {/* Upgrade banner for buyer-only accounts */}
      {isAdmin && !hasSubscription && (
        <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-blue-800 dark:text-blue-300">Want to sell vehicles?</p>
            <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">Upgrade to a seller subscription to create and send deals.</p>
          </div>
          <button onClick={() => navigate(`/upgrade?plan=${SELLER_PLAN}`)} className="shrink-0 px-3 py-1.5 text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors">
            Upgrade
          </button>
        </div>
      )}

      {/* Tab bar */}
      <div className="flex border-b border-slate-200 dark:border-slate-700 mb-6 overflow-x-auto">
        {tabs.map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`shrink-0 whitespace-nowrap flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === t
                ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'Subscription' && (
        <SubscriptionTab account={account} locationCount={locations.length || 1} onUpdate={handleAccountUpdate} />
      )}
      {activeTab === 'Locations' && (
        <LocationsTab account={account} locations={locations} onLocationsChange={setLocations} currentLocationId={appUser?.location_id} />
      )}
      {activeTab === 'Team' && (
        <TeamTab account={account} locations={locations} />
      )}
      {activeTab === 'Buyers' && (
        <BuyersTab locationId={appUser?.location_id} locationName={appUser?.location?.name} />
      )}
      {activeTab === 'Notifications' && <NotificationsTab />}
      {activeTab === 'Support' && <SupportTab />}
      <LeaveTeam accountName={account?.name} />
    </div>
  );
}
